import { createFileRoute, Outlet, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import { SubTabs } from '@/components/shared/SubTabs';
import { PageSubTabsProvider } from '@/hooks/usePageSubTabs';
import { useTranslator } from '@/hooks/useTranslator';
import { fallback } from '@/lib/utils/zodFallback';

/**
 * Shared by both sub-tabs: the same table state, whichever list is open.
 *
 * On the `(list)` layout rather than the whole feature, so the Active /
 * Archived toggle belongs to the two lists and does not follow the operator
 * onto a certificate's detail page, where there is nothing to toggle.
 */
export const certificationsListSearch = z.object({
  page: fallback(z.number().int().min(1), 1),
  limit: fallback(z.number().int().min(10).max(500), 50),
  sort: z
    .string()
    .transform(v => (/^\w+:(ASC|DESC)$/.test(v) ? v : undefined))
    .optional(),
  filters: z
    .union([
      z.string().transform(v => {
        try {
          return JSON.parse(v) as Record<string, string[]>;
        } catch {
          return undefined;
        }
      }),
      z.record(z.string(), z.array(z.string())),
    ])
    .optional(),
});

export const Route = createFileRoute(
  '/management/certifications/(pages)/(list)',
)({
  validateSearch: certificationsListSearch,
  beforeLoad: ({ location }) => {
    // The bare path has no list of its own — Active is the one an operator
    // wants nine times in ten.
    if (location.pathname.replace(/\/$/, '').endsWith('/certifications')) {
      throw redirect({ to: '/management/certifications/active' });
    }
  },
  component: ManagementCertificationsLayout,
});

function ManagementCertificationsLayout() {
  const { getTranslation: t } = useTranslator({
    page: 'managementCertifications',
  });

  return (
    <PageSubTabsProvider
      tabs={
        <SubTabs
          tabs={[
            {
              key: 'active',
              label: t('toggle.active'),
              to: '/management/certifications/active',
            },
            {
              key: 'archived',
              label: t('toggle.archived'),
              to: '/management/certifications/archived',
            },
          ]}
        />
      }
    >
      <Outlet />
    </PageSubTabsProvider>
  );
}
